import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Truck, Users, Building2, FileText, AlertCircle,
  LogOut, Menu, UserCircle, Database, Upload, Settings, AlertTriangle,
  ShieldCheck, BarChart3, DollarSign, Shield, Bot, Trophy, Calculator,
  Settings2, ScrollText, Bell, UserPlus, ChevronDown, ChevronRight,
  PanelLeftClose, PanelLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePermissions } from '@/contexts/PermissionsContext';
import FloatingChatWidget from '@/components/FloatingChatWidget';
import api from '@/lib/api';

const sidebarGroups = [
  {
    id: 'overview',
    label: 'Vue d\'ensemble',
    items: [
      { path: '/', label: 'Tableau de bord', icon: LayoutDashboard, resource: 'dashboard' },
    ],
  },
  {
    id: 'intelligence',
    label: 'Intelligence & Analyse',
    items: [
      { path: '/copilot-ia', label: 'Copilote IA', icon: Bot, highlight: true },
      { path: '/analyse-chauffeurs', label: 'Analyse Chauffeurs', icon: Trophy },
      { path: '/simulation-operateur', label: 'Simulation Tarif', icon: Calculator },
      { path: '/kpi-phase1', label: 'KPI Operationnels', icon: BarChart3, resource: 'kpi' },
    ],
  },
  {
    id: 'exploitation',
    label: 'Exploitation',
    items: [
      { path: '/operations', label: 'Operations', icon: FileText, resource: 'operations' },
      { path: '/chauffeurs', label: 'Chauffeurs', icon: UserCircle, resource: 'chauffeurs' },
      { path: '/vehicules', label: 'Vehicules', icon: Truck, resource: 'vehicules' },
      { path: '/incidents', label: 'Incidents', icon: AlertTriangle, resource: 'incidents' },
      { path: '/alerts', label: 'Alertes', icon: AlertCircle, resource: 'alerts' },
    ],
  },
  {
    id: 'referentiel',
    label: 'Referentiel',
    items: [
      { path: '/proprietaires', label: 'Partenaires', icon: Building2, resource: 'proprietaires' },
      { path: '/societes', label: 'Clients', icon: Users, resource: 'societes' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    items: [
      { path: '/finance', label: 'Finance', icon: DollarSign, resource: 'finance' },
    ],
  },
  {
    id: 'admin',
    label: 'Administration',
    items: [
      { path: '/users', label: 'Utilisateurs', icon: Users, resource: 'users' },
      { path: '/access-requests', label: 'Demandes d\'acces', icon: UserPlus, resource: 'users', badge: true },
      { path: '/import', label: 'Import', icon: Upload, resource: 'import' },
      { path: '/rbac-management', label: 'Roles & Permissions', icon: Shield, resource: 'rbac' },
      { path: '/audit-logs', label: 'Journal d\'activite', icon: ShieldCheck, resource: 'audit_logs' },
      { path: '/system-logs', label: 'Sante systeme', icon: ScrollText, resource: 'audit_logs' },
      { path: '/sauvegardes', label: 'Sauvegardes', icon: Database, resource: 'backups' },
      { path: '/settings', label: 'Parametres', icon: Settings2, resource: 'settings' },
      { path: '/settings/notifications', label: 'Notifications', icon: Bell, resource: 'settings' },
    ],
  },
];

const Layout = ({ user, setUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingAccessCount, setPendingAccessCount] = useState(0);

  // Collapsed groups state — persisted in localStorage
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_collapsed');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', JSON.stringify(collapsedGroups));
  }, [collapsedGroups]);

  // Persist sidebar open state
  useEffect(() => {
    try {
      const saved = localStorage.getItem('sidebar_open');
      if (saved !== null) setSidebarOpen(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem('sidebar_open', JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  // Fetch pending access requests count
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const resp = await api.get('/access-requests/count');
        setPendingAccessCount(resp.data.count || 0);
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // Filter items by RBAC permission
  const getVisibleItems = (items) => {
    return items.filter(item => {
      if (!item.resource) return true;
      const action = item.resource === 'import' ? 'import' : 'read';
      return hasPermission(item.resource, action);
    });
  };

  // Check if current path is in a group (auto-expand active group)
  const isGroupActive = (group) => {
    return group.items.some(item => location.pathname === item.path);
  };

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;

    return (
      <Link
        key={item.path}
        to={item.path}
        data-testid={`nav-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
        onClick={() => setMobileMenuOpen(false)}
        className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm relative ${
          isActive
            ? item.highlight
              ? 'bg-blue-600 text-white font-medium'
              : 'bg-emerald-50 text-emerald-700 font-medium'
            : item.highlight
            ? 'text-blue-700 bg-blue-50/50 hover:bg-blue-50'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`}
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
        {sidebarOpen && (
          <span className="flex-1 flex items-center justify-between truncate">
            <span className="truncate">{item.label}</span>
            {item.badge && pendingAccessCount > 0 && (
              <span
                className="ml-2 bg-red-500 text-white text-[10px] font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1"
                data-testid="pending-access-badge"
              >
                {pendingAccessCount}
              </span>
            )}
          </span>
        )}
        {!sidebarOpen && item.badge && pendingAccessCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-[16px] flex items-center justify-center px-0.5">
            {pendingAccessCount}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          bg-white border-r border-gray-200 transition-all duration-300 flex flex-col
          ${sidebarOpen ? 'w-60' : 'w-[60px]'}
          md:relative fixed inset-y-0 left-0 z-50
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
        role="navigation"
        aria-label="Navigation principale"
      >
        {/* Header */}
        <div className={`flex items-center border-b border-gray-100 ${sidebarOpen ? 'justify-between px-4 py-3' : 'justify-center py-3'}`}>
          {sidebarOpen && (
            <span className="text-lg font-bold text-emerald-600 truncate">Baobab Connect</span>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            data-testid="sidebar-toggle-btn"
            title={sidebarOpen ? 'Replier la sidebar' : 'Deployer la sidebar'}
          >
            {sidebarOpen ? <PanelLeftClose className="h-5 w-5" /> : <PanelLeft className="h-5 w-5" />}
          </button>
        </div>

        {/* Navigation groups */}
        <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-1 scrollbar-thin">
          {sidebarGroups.map((group) => {
            const visibleItems = getVisibleItems(group.items);
            if (visibleItems.length === 0) return null;

            const isCollapsed = collapsedGroups[group.id] && !isGroupActive(group);
            const active = isGroupActive(group);

            // Collapsed sidebar: just icons
            if (!sidebarOpen) {
              return (
                <div key={group.id} className="space-y-0.5">
                  {visibleItems.map(renderNavItem)}
                </div>
              );
            }

            return (
              <div key={group.id}>
                {/* Group header */}
                <button
                  onClick={() => toggleGroup(group.id)}
                  className={`w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-md transition-colors ${
                    active ? 'text-emerald-700' : 'text-gray-400 hover:text-gray-600'
                  }`}
                  data-testid={`group-toggle-${group.id}`}
                >
                  <span>{group.label}</span>
                  {isCollapsed ? (
                    <ChevronRight className="h-3 w-3" />
                  ) : (
                    <ChevronDown className="h-3 w-3" />
                  )}
                </button>

                {/* Group items */}
                {!isCollapsed && (
                  <div className="space-y-0.5 mt-0.5">
                    {visibleItems.map(renderNavItem)}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="border-t border-gray-100 p-3">
          {sidebarOpen ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-sm flex-shrink-0">
                  {user?.nom?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user?.nom || 'Utilisateur'}</p>
                  <p className="text-xs text-gray-400">{user?.role || 'N/A'}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4" />
                Deconnexion
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full flex justify-center p-2 text-gray-400 hover:text-red-600 rounded-md transition-colors"
              data-testid="logout-icon-btn"
              title="Deconnexion"
            >
              <LogOut className="h-5 w-5" />
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        {/* Mobile header */}
        <div className="md:hidden sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <span className="text-lg font-bold text-emerald-600">Baobab Connect</span>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Ouvrir le menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-4 md:p-6">
          <Outlet />
        </div>
      </main>

      <FloatingChatWidget />
    </div>
  );
};

export default Layout;
